import { EnvironmentSensorType } from '@mgh-app/component-library';
import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AirQualitySensor,
  AllAirQualitySensors,
  AwayMode,
  EnergyUsage,
  EnergyUsageInPeriod,
  Light,
  LightingPreset,
  Schedule,
  Shade,
  Temperature,
  Thing,
} from 'models/Thing';
import { RootState } from 'store';
import { authFetchBaseQuery, baseUrl } from './apiConfig';
import { ResponseBase } from './request';
import { DateTime } from 'luxon';

const baseEndpointUrl = `${baseUrl}`;
const apiPrefix = 'things';

const keepUnusedApiDataInSeconds = 0.1; // 100 ms

interface ThingListResult {
  createdAt: string;
  fk_room_id: string;
  id: string;
  identifier: string;
  image: string;
  isParent: number;
  name: string;
  parent_id: string;
  position: number;
  status: number;
  thing_type: string;
  updatedAt: string;
}

interface ThingsResponse<R> extends ResponseBase {
  error: boolean;
  data: R[];
}

interface ThingInfoRequest {
  roomId: string;
  thingId: string;
}

export type PowerState = 'ON' | 'OFF';
export type HeaterState = 'ON' | 'OFF';

interface LightValue {
  brightness: number;
  power: PowerState;
}

interface LightResponse extends ResponseBase {
  data: { [key: string]: LightValue };
}

interface SetLightValueRequest {
  thing_ids: string[];
  power: PowerState;
  brightness: number;
}

interface Preset {
  [key: string]: number;
}
export interface GetLightingPresetsResponse extends ResponseBase {
  data: { [key: string]: Preset };
}

export type GetAllLightingValuesResponse = LightResponse;

interface SensorValue {
  label?: string;
  rh?: number;
  co2?: number;
  dew?: number;
  lux?: number;
  pm10?: number;
  voc?: number;
  pm25?: number;
  air_pressure?: number;
  temp: number;
}

interface SensorsResponse extends ResponseBase {
  data: SensorValue[];
}

interface AllSensorsResponse extends ResponseBase {
  data: { [key: string]: SensorValue[] };
  timestamp: number;
}

interface ShadeValue {
  position: number;
  favourite: number;
}

interface ShadeResponse extends ResponseBase {
  data: { [key: string]: ShadeValue };
}

interface SetShadeValueRequest {
  thing_id: string;
  position: number;
  favourite: number;
}

export interface PresetRequest {
  groupId: string;
  name: string;
}

export interface CreatePresetRequest extends PresetRequest {
  presets: Preset;
}

export interface ModifyPresetRequest extends CreatePresetRequest {
  presetId: string;
}

export interface ChildrenGroup extends ThingListResult {
  children: ThingListResult[];
}

export interface GetChildrenData {
  id: string;
  groups: ChildrenGroup[];
}

export interface GetChildrenResponse extends ResponseBase {
  data: GetChildrenData;
}

interface TemperatureData {
  setPoint: number;
  temperature: number;
  heater: HeaterState;
  schedule?: Schedule;
  away?: AwayMode;
  isScheduled?: boolean;
  overrideEndTimestamp?: number;
}

interface TemperatureResponse extends ResponseBase {
  data: { [key: string]: TemperatureData };
}

interface SetTemperatureValueRequest {
  thing_id: string;
  argValue: number;
}

interface EnergyUsageData {
  days: number[];
  months: number[];
}

type EnergyAllUsageResponse = ResponseBase & {
  data: EnergyUsageData;
};

interface SetTemperatureScheduleRequest {
  thingId: string;
  schedule: Schedule;
}

interface SetTemperatureAwayModeRequest {
  thingId: string;
  away: { setPoint: number };
}

interface HomeAwayModeResponse extends ResponseBase {
  data: AwayMode;
}

const Tags = {
  individualLight: 'individual-light',
  temperature: 'temperature',
};

export const thingsApi = createApi({
  reducerPath: 'thingsApi',
  tagTypes: [Tags.individualLight, Tags.temperature],
  baseQuery: authFetchBaseQuery({
    baseUrl: baseEndpointUrl,
  }),
  endpoints: (builder) => ({
    getThingsList: builder.query<Thing[], string>({
      query: (roomId) => ({ url: `${apiPrefix}/list`, method: 'POST', body: { roomId } }),
      transformResponse: (response: ThingsResponse<ThingListResult>) =>
        response.data.map((thing) => ({
          createdAt: thing.createdAt,
          roomId: thing.fk_room_id,
          id: thing.id,
          identifier: thing.identifier,
          image: thing.image,
          isParent: thing.isParent,
          name: thing.name,
          parentId: thing.parent_id,
          position: thing.position,
          status: thing.status,
          thingType: thing.thing_type,
          updatedAt: thing.updatedAt,
        })),
    }),
    getThingInfo: builder.query<Thing | null, ThingInfoRequest>({
      query: ({ roomId }) => ({ url: `${apiPrefix}/list`, method: 'POST', body: { roomId } }),
      transformResponse: (response: ThingsResponse<ThingListResult>, _, { thingId }) => {
        const thing = response.data.find((t) => t.id === thingId);
        if (!thing) {
          return null;
        }
        return {
          createdAt: thing.createdAt,
          roomId: thing.fk_room_id,
          id: thing.id,
          identifier: thing.identifier,
          image: thing.image,
          isParent: thing.isParent,
          name: thing.name,
          parentId: thing.parent_id,
          position: thing.position,
          status: thing.status,
          thingType: thing.thing_type,
          updatedAt: thing.updatedAt,
        };
      },
    }),
    getChildThings: builder.query<Thing[], string>({
      query: (parentThingId) => ({ url: `${apiPrefix}/list/${parentThingId}` }),
      transformResponse: (response: ThingsResponse<ThingListResult>) => {
        return response.data.map((thing) => ({
          createdAt: thing.createdAt,
          roomId: thing.fk_room_id,
          id: thing.id,
          identifier: thing.identifier,
          image: thing.image,
          isParent: thing.isParent,
          name: thing.name,
          parentId: thing.parent_id,
          position: thing.position,
          status: thing.status,
          thingType: thing.thing_type,
          updatedAt: thing.updatedAt,
        }));
      },
    }),
    getLight: builder.query<Light, string>({
      query: (lightId) => ({ url: `${apiPrefix}/lighting/get/${lightId}` }),
      transformResponse: (response: LightResponse, _, lightId) => {
        const light = response.data[lightId];
        return { lightIds: [lightId], brightness: light.brightness, power: light.power };
      },
      forceRefetch: () => true,
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
    setLightValue: builder.mutation<LightResponse, Light>({
      query: (light) => ({
        url: `${apiPrefix}/lighting/update`,
        method: 'POST',
        body: {
          thing_ids: light.lightIds,
          power: light.power,
          brightness: light.brightness,
        } as SetLightValueRequest,
      }),
    }),
    getLightingPresets: builder.query<LightingPreset[], string>({
      query: (groupId: string) => ({
        url: `${apiPrefix}/lighting/presets/${groupId}`,
      }),
      transformResponse: (response: GetLightingPresetsResponse) =>
        Object.entries(response.data).map(
          ([name, preset]) => ({ name, lights: preset } as LightingPreset),
        ),
      forceRefetch: () => true,
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
    setLightingPreset: builder.mutation<ResponseBase, LightingPreset>({
      query: (preset) => ({
        url: `${apiPrefix}/lighting/update/groups`,
        method: 'POST',
        body: {
          data: preset.lights,
        },
      }),
    }),
    getAllLightingValues: builder.query<Light[], void>({
      query: () => ({ url: `${apiPrefix}/lighting/get`, method: 'GET' }),
      transformResponse: (response: GetAllLightingValuesResponse) =>
        Object.entries(response.data).map(([lightId, value]) => ({
          lightIds: [lightId],
          brightness: value.brightness,
          power: value.power,
        })),
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
    getAirQualityAllSensors: builder.query<AllAirQualitySensors, void>({
      query: () => ({ url: `${apiPrefix}/sensors/get/all` }),
      transformResponse: (response: AllSensorsResponse) => {
        return {
          timestamp: response.timestamp,
          sensors: Object.fromEntries(
            Object.entries(response.data).map(([key, value]) => [
              key,
              value.reduce(
                (sensors, sensor) => mapSensorValue(sensor, sensors),
                [] as AirQualitySensor[],
              ),
            ]),
          ),
        };
      },
      forceRefetch: () => true,
    }),
    getAirQualitySensors: builder.query<AirQualitySensor[], string>({
      query: (sensorsId) => ({ url: `${apiPrefix}/sensors/get/${sensorsId}` }),
      transformResponse: (response: SensorsResponse) => {
        const sensors: AirQualitySensor[] = [];
        response.data.forEach((sensor) => mapSensorValue(sensor, sensors));
        return sensors;
      },
      forceRefetch: () => true,
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
    getShadesValues: builder.query<Shade, string>({
      query: (shadesId) => ({ url: `${apiPrefix}/shading/get/${shadesId}` }),
      transformResponse: (response: ShadeResponse, _, shadesId) => {
        const shades = response.data[shadesId];
        return { shadeId: shadesId, position: shades.position, favourite: shades.favourite };
      },
      forceRefetch: () => true,
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
    setShadesValue: builder.mutation<ShadeResponse, Shade>({
      query: (shades) => ({
        url: `${apiPrefix}/shading/update`,
        method: 'POST',
        body: {
          thing_id: shades.shadeId,
          position: shades.position,
          favourite: shades.favourite,
        } as SetShadeValueRequest,
      }),
    }),
    getChildren: builder.query<GetChildrenData, string>({
      query: (groupId) => ({
        url: `${apiPrefix}/dmx/parent`,
        method: 'POST',
        body: {
          thing_id: groupId,
        },
      }),
      transformResponse: (response: GetChildrenResponse) => response.data,
    }),
    createNewPreset: builder.mutation<ResponseBase, CreatePresetRequest>({
      query: (arg) => ({
        url: `${apiPrefix}/lighting/presets/${arg.groupId}/${arg.name}`,
        method: 'POST',
        body: {
          data: Object.fromEntries(
            Object.entries(arg.presets).map(([key, val]) => [key, Math.round(val)]),
          ),
        },
      }),
    }),
    updatePreset: builder.mutation<ResponseBase, ModifyPresetRequest>({
      query: (arg) => ({
        url: `${apiPrefix}/lighting/presets/${arg.groupId}/${arg.presetId}`,
        method: 'PUT',
        body: {
          name: arg.name,
          data: Object.fromEntries(
            Object.entries(arg.presets).map(([key, val]) => [key, Math.round(val)]),
          ),
        },
      }),
    }),
    deletePreset: builder.mutation<ResponseBase, PresetRequest>({
      query: (arg) => ({
        url: `${apiPrefix}/lighting/presets/${arg.groupId}/${arg.name}`,
        method: 'DELETE',
      }),
    }),
    getTemperatureValues: builder.query<Temperature, string>({
      query: (thingId) => ({ url: `${apiPrefix}/heater/get/${thingId}` }),
      transformResponse: (response: TemperatureResponse, _, thingId) => {
        const temperatureData = response.data[thingId];
        return {
          thingId: thingId,
          temperature: temperatureData.temperature,
          setPoint: temperatureData.setPoint,
          heater: temperatureData.heater,
          schedule: temperatureData.schedule,
          away: temperatureData.away,
          isScheduled: temperatureData.isScheduled,
          overrideEndTimestamp: temperatureData.overrideEndTimestamp,
        };
      },
      forceRefetch: () => true,
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
    setTemperatureValues: builder.mutation<TemperatureResponse, Temperature>({
      query: (temperature) => ({
        url: `${apiPrefix}/heater/update`,
        method: 'POST',
        body: {
          thing_id: temperature.thingId,
          argValue: temperature.setPoint,
          overrideEndTimestamp: temperature.overrideEndTimestamp,
        } as SetTemperatureValueRequest,
      }),
      onQueryStarted: async (temperature, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          thingsApi.util.updateQueryData('getTemperatureValues', temperature.thingId, (draft) => {
            Object.assign(draft, temperature);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    setTemperatureSchedule: builder.mutation<ResponseBase, SetTemperatureScheduleRequest>({
      query: (temperatureSchedule) => ({
        url: `${apiPrefix}/heater/${temperatureSchedule.thingId}/schedule`,
        method: 'POST',
        body: { schedule: temperatureSchedule.schedule },
      }),
    }),
    setTemperatureAwayMode: builder.mutation<ResponseBase, SetTemperatureAwayModeRequest>({
      query: (temperatureAwayMode) => ({
        url: `${apiPrefix}/heater/${temperatureAwayMode.thingId}/away`,
        method: 'POST',
        body: { away: temperatureAwayMode.away },
      }),
    }),
    getAwayMode: builder.query<AwayMode, void>({
      query: () => ({
        url: `${apiPrefix}/heater/away`,
      }),
      transformResponse: (response: HomeAwayModeResponse) => response.data,
    }),
    setAwayMode: builder.mutation<ResponseBase, AwayMode>({
      query: (away) => ({
        url: `${apiPrefix}/heater/away`,
        method: 'POST',
        body: { away },
      }),
    }),
    getEnergyAll: builder.query<EnergyUsage, void>({
      query: () => ({ url: `${apiPrefix}/energy-usage/get/all` }),
      transformResponse: (response: EnergyAllUsageResponse) => {
        const {
          data: { days: totalDays, months: totalMonths },
        } = response;

        const utcNow = DateTime.utc();

        return {
          energyUseToday: totalDays[0],
          weekly: calcEnergyUsageWeekly(utcNow, totalDays),
          monthly: calcEnergyUsageMonthly(utcNow, totalDays, totalMonths),
          yearly: calcEnergyUsageAnnual(utcNow, totalMonths),
        };
      },
      forceRefetch: () => true,
      keepUnusedDataFor: keepUnusedApiDataInSeconds,
    }),
  }),
});

export const {
  useGetThingsListQuery,
  useGetThingInfoQuery,
  useGetLightQuery,
  useSetLightValueMutation,
  useGetLightingPresetsQuery,
  useSetLightingPresetMutation,
  useGetAllLightingValuesQuery,
  useGetAirQualitySensorsQuery,
  useGetAirQualityAllSensorsQuery,
  useGetChildThingsQuery,
  useGetShadesValuesQuery,
  useSetShadesValueMutation,
  useGetChildrenQuery,
  useCreateNewPresetMutation,
  useUpdatePresetMutation,
  useDeletePresetMutation,
  useGetTemperatureValuesQuery,
  useSetTemperatureValuesMutation,
  useSetTemperatureScheduleMutation,
  useSetTemperatureAwayModeMutation,
  useGetAwayModeQuery,
  useLazyGetAwayModeQuery,
  useSetAwayModeMutation,
  useGetEnergyAllQuery,
  useLazyGetEnergyAllQuery,
} = thingsApi;

export const selectThingsByRoomId = (state: RootState, roomId: string) =>
  thingsApi.endpoints.getThingsList.select(roomId)(state)?.data ?? [];

export const selectChildren = (state: RootState, groupId: string) =>
  thingsApi.endpoints.getChildren.select(groupId)(state)?.data ?? [];

export const selectPreset = (state: RootState, groupId: string, presetName: string) =>
  thingsApi.endpoints.getLightingPresets
    .select(groupId)(state)
    .data?.find((preset) => preset.name === presetName);

export const selectThing = createSelector(
  [
    (state: RootState, roomId: string, thingId: string) => thingId,
    (state: RootState, roomId: string) => roomId,
    (state: RootState, roomId: string) => selectThingsByRoomId(state, roomId),
  ],
  (thingId: string, roomId: string, things: Thing[]) => things.find((t) => t.id === thingId),
);

export const selectSensors = (state: RootState, sensorId: string) =>
  thingsApi.endpoints.getAirQualityAllSensors.select()(state).data?.sensors[sensorId];

export const selectEnergyUsage = (state: RootState) =>
  thingsApi.endpoints.getEnergyAll.select(undefined)(state).data;

const mapSensorValue = (sensor: SensorValue, sensors: AirQualitySensor[]) => {
  const sensorProps = Object.getOwnPropertyNames(sensor);
  for (const prop of sensorProps) {
    switch (prop) {
      case 'rh':
        sensors.push({
          type: EnvironmentSensorType.RH,
          value: sensor['rh'],
          label: sensor['label'],
        });
        break;
      case 'co2':
        sensors.push({
          type: EnvironmentSensorType.CO2,
          value: sensor['co2'],
          label: sensor['label'],
        });
        break;
      case 'voc':
        sensors.push({
          type: EnvironmentSensorType.VOC,
          value: sensor['voc'],
          label: sensor['label'],
        });
        break;
      case 'dew':
        sensors.push({
          type: EnvironmentSensorType.Dew,
          value: sensor['dew'],
          label: sensor['label'],
        });
        break;
      case 'lux':
        sensors.push({
          type: EnvironmentSensorType.Lux,
          value: sensor['lux'],
          label: sensor['label'],
        });
        break;
      case 'pm25':
        sensors.push({
          type: EnvironmentSensorType.PM25,
          value: sensor['pm25'],
          label: sensor['label'],
        });
        break;
      case 'pm10':
        sensors.push({
          type: EnvironmentSensorType.PM10,
          value: sensor['pm10'],
          label: sensor['label'],
        });
        break;
      case 'air_pressure':
        sensors.push({
          type: EnvironmentSensorType.AirPressure,
          value: sensor['air_pressure'],
          label: sensor['label'],
        });
        break;
      case 'temp':
        sensors.push({
          type: EnvironmentSensorType.Temperature,
          value: sensor['temp'],
          label: sensor['label'],
        });
        break;
    }
  }

  return sensors;
};

const calculatePeriodAverage = (period: number[], includeCurrent = false): number => {
  const validPeriod = [...period];
  // const validPeriod = includeCurrent ? [...period] : period.slice(1); // remove current day/month
  // remove all trailing 0 values, i.e [1,0,1,2,0,0,0] => [1,0,1,2]
  while (validPeriod[validPeriod.length - 1] === 0) {
    validPeriod.pop();
  }

  if (!includeCurrent) {
    validPeriod.pop();
  }

  let average = 0;
  if (validPeriod.length !== 0) {
    average = validPeriod.reduce((a, b) => a + b, 0) / validPeriod.length;
  }

  return Number(average.toFixed());
};

const calcEnergyUsageAnnual = (utcNow: DateTime, totalMonths: number[]): EnergyUsageInPeriod => {
  const monthInYear = utcNow.month;
  const yearlyData = totalMonths.slice(0, monthInYear).reverse();
  yearlyData.length = 12;
  const yearlyUse = Array.from(yearlyData, (v) => v ?? 0);
  const prevYearlyData = totalMonths.slice(monthInYear, monthInYear + (12 - monthInYear));
  prevYearlyData.length = 12;
  const prevYearlyUse = Array.from(prevYearlyData, (v) => v ?? 0);

  const averageYearly = calculatePeriodAverage(yearlyUse);
  const averagePrevYearly = calculatePeriodAverage(prevYearlyUse, true);

  return {
    average: averageYearly,
    total: yearlyUse.reduce((acc, item) => acc + item, 0),
    usage: averageYearly / averagePrevYearly - 1,
    data: yearlyUse,
  };
};

const calcEnergyUsageMonthly = (
  utcNow: DateTime,
  totalDays: number[],
  totalMonths: number[],
): EnergyUsageInPeriod => {
  const startOfMonth = utcNow.startOf('month');

  const perWeek: number[][] = [];
  for (let i = 0; i < utcNow.daysInMonth; i++) {
    const date = startOfMonth.plus({ days: i });
    const weekNo = date.weekNumber - startOfMonth.weekNumber;
    perWeek[weekNo] = [
      ...(perWeek[weekNo] ?? []),
      utcNow.day >= date.day ? totalDays[utcNow.day - date.day] : 0,
    ];
  }

  const monthlyUse = perWeek.map((week, index) =>
    calculatePeriodAverage(week, utcNow.weekNumber !== startOfMonth.weekNumber + index),
  );
  const monthlyAverage = calculatePeriodAverage(perWeek.flatMap((d) => d));
  const monthlyAverageFromCompletedWeeks = calculatePeriodAverage(
    perWeek.flatMap((d) => d).slice(0, utcNow.startOf('week').day - 1),
  );

  return {
    average: monthlyAverage,
    total: totalMonths[0],
    usage: monthlyAverageFromCompletedWeeks / totalMonths[1] - 1,
    data: monthlyUse,
  };
};

const calcEnergyUsageWeekly = (utcNow: DateTime, totalDays: number[]): EnergyUsageInPeriod => {
  const weekday = utcNow.weekday;
  const week = totalDays.slice(0, weekday).reverse();
  week.length = 7;

  const weeklyUse = Array.from(week, (v) => v ?? 0);
  const prevWeeklyUse = totalDays.slice(weekday, weekday + 7).reverse();
  const averageWeekly = calculatePeriodAverage(weeklyUse);
  const averagePrevWeek = calculatePeriodAverage(prevWeeklyUse, true);

  return {
    average: averageWeekly,
    total: weeklyUse.reduce((acc, item) => acc + item, 0),
    usage: averageWeekly / averagePrevWeek - 1,
    data: weeklyUse,
  };
};
